import type { ReactNode } from 'react';
import { Link } from '@src/components';

export interface FaqCopy<T extends ReactNode = ReactNode> {
  ru: T;
  en: T;
}

export function copy<T extends ReactNode>(ru: T, en: T): FaqCopy<T> {
  return { ru, en };
}

export function faqLink(href: string, label: string) {
  return (
    <Link href={href} variant="inline">
      {label}
    </Link>
  );
}

export interface FaqQuestion {
  question: FaqCopy<string>;
  search?: string;
  answer: FaqCopy;
  ruOnly?: boolean;
  comOnly?: boolean;
}

const qNoEmailInbox = {
  question: copy(
    'Мне на почту не доходят письма, что делать?',
    'I am not receiving emails, what should I do?',
  ),
  search:
    ' emails email receiv sent пришли пись письм письма прих приходят дошли писем пришло приш письмо приходит дошло дошл персональная ссылка коды восстановления personal link recovery codes',
  answer: copy(
    <>
      <p>
        Письмо не обязательно. Попросите организатора прислать персональную ссылку на вашу карточку
        — её можно открыть без email и регистрации.
      </p>
      <p>
        Если вы заходили без почты, сохраните коды восстановления: без них можно потерять доступ
        при смене браузера или устройства. Если входили через email, доступ можно восстановить
        письмом.
      </p>
      <p>
        Если вы всё же ждали письмо, проверьте папки «спам» и «рассылки» и поищите отправителя{' '}
        <b>postman@santa-secret.ru</b>.
      </p>
    </>,
    <>
      <p>
        Email is not required. Ask the organizer to send the personal link to your card — you can
        open it without email or registration.
      </p>
      <p>
        If you joined without email, save your recovery codes: without them you can lose access
        when the browser or device changes. If you signed in with email, you can recover through
        that inbox.
      </p>
      <p>
        If you were still waiting for an email, check “spam” and “promotions” and look for{' '}
        <b>postman@santa-secret.com</b>.
      </p>
    </>,
  ),
};

const qNoEmailInboxOrg = {
  question: copy(
    'После отправления приглашений участникам не дошли письма, что делать?',
    'What to do if the invitation emails did not reach the participants after being sent?',
  ),
  search:
    ' emails email receiv sent пришли пись письм письма прих приходят дошли писем пришло приш письмо приходит дошло дошл персональная ссылка коды восстановления personal link recovery codes',
  answer: copy(
    <>
      <p>
        Письмо не обязательно. Скопируйте персональную ссылку участника в таблице или на его
        карточке и отправьте её лично — в мессенджере или в чат. Регистрация и email не нужны.
      </p>
      <p>
        Если человек заходит без почты, попросите сохранить коды восстановления: без них можно
        потерять доступ при смене браузера или устройства.
      </p>
    </>,
    <>
      <p>
        Email is not required. Copy the participant’s personal link from the table or their card
        and send it directly — in a messenger or chat. Registration and email are optional.
      </p>
      <p>
        If they join without email, ask them to save their recovery codes. Without the codes they
        can lose access if the browser or device changes.
      </p>
    </>,
  ),
};

const qWhenIWillKnowWard = {
  question: copy(
    'Я заполнил свою карточку для участия в игре. Когда я узнаю, кому дарить подарок?',
    'I filled out my card to participate in the game. When will I know whom to give a gift to?',
  ),
  search: 'when draw conduct когда будет жеребьёвка кому дарить подарок подопечный',
  answer: copy(
    <>
      Жеребьёвку проводит организатор, а не сайт. Когда он её запустит, подопечный появится у вас
      в игре. Если вы указали email, туда тоже может прийти уведомление — но смотреть результат
      можно и без почты.
    </>,
    <>
      The organizer runs the draw, not the site. When they start it, your giftee will appear in
      the game. If you added an email, you may also get a notification — but you can see the
      result without one.
    </>,
  ),
};

const qOddNumber = {
  question: copy(
    'Работает ли жеребьёвка для нечетного количества участников? Никто не останется без подарка?',
    'Does the draw work for an odd number of participants? Will no one be left without a gift?',
  ),
  search:
    'нечетное количество участ участников четное количество участников odd number of participants even number of participants',
  answer: copy(
    <>
      Да, работает! Нечетное количество ничем, в данном случае, не отличается от четного. Покажем на
      примере троих людей. Допустим, в жеребьёвке участвуют Маша, Вася и Петя, тогда после
      жеребьёвки одним из возможных вариантов распределения будет: Маша дарит Васе, Вася дарит Пете,
      а Петя дарит Маше. Как видите, все будут с подарками!
    </>,
    <>
      Yes, it works! An odd number of participants does not differ from an even number in this case.
      Let's illustrate with three people. Suppose Masha, Vasya, and Petya participate in the draw,
      then one of the possible distributions after the draw could be: Masha gifts to Vasya, Vasya
      gifts to Petya, and Petya gifts to Masha. As you can see, everyone will have gifts!
    </>,
  ),
};

const qWhatIsSanta = {
  question: copy('Что такое «Тайный Санта»?', 'What is "Secret Santa"?'),
  search: 'анонимный обмен подарками организатор anonymous gift exchange organizer',
  answer: copy(
    <>
      Тайный Санта, или Тайный Дед Мороз, — это игра с анонимным обменом подарками. Каждый участник
      случайным образом получает человека, для которого готовит подарок, а кто кому выпал, до
      жеребьёвки остаётся секретом.
      <br />
      <br />
      Santa-Secret помогает организовать всю игру онлайн: пригласить участников, собрать пожелания,
      провести жеребьёвку и следить за ходом обмена.
      <br />
      <br />
      <b>
        Нужна только жеребьёвка? Используйте{' '}
        {faqLink('/randomizer', 'бесплатный рандомайзер Тайного Санты')}.
      </b>{' '}
      Добавьте участников и случайным образом распределите, кто кому дарит подарок.
    </>,
    <>
      Secret Santa is a game with an anonymous gift exchange. Each person is randomly assigned
      someone to prepare a gift for, and who got whom stays secret until the draw.
      <br />
      <br />
      Santa-Secret helps you organize the whole game online: invite people, collect wishes, run the
      draw, and follow the exchange.
      <br />
      <br />
      <b>
        Only need the draw? Use our {faqLink('/randomizer', 'free Secret Santa randomizer')}.
      </b>{' '}
      Add names and randomly assign who gives a gift to whom.
    </>,
  ),
};

const qWhatIsThisSiteFor = {
  question: copy('Зачем и для кого был создан этот сайт?', 'Why and for whom was this site created?'),
  search: 'бумажки бумажка шапка шляпа онлайн pieces of paper hat online',
  answer: copy(
    <>
      Santa-Secret создан для взрослых, которые хотят устроить Тайного Санту для друзей, коллег,
      семьи или другой компании — и не тратить праздник на организационную суету.
      <br />
      <br />
      Участникам достаточно присоединиться по ссылке и рассказать о своих пожеланиях, а организатор
      может настроить жеребьёвку, следить за ходом игры и помогать, если что-то пойдёт не по плану.
    </>,
    <>
      Santa-Secret is for adults who want to run Secret Santa for friends, coworkers, family, or
      another group — without spending the holiday on organizing chaos.
      <br />
      <br />
      Participants just join by link and share their wishes. The organizer can set up the draw,
      follow the game, and help if something does not go as planned.
    </>,
  ),
};

const qWhatIsTheBox = {
  question: copy('Что означает «коробка»?', 'What does "box" mean?'),
  search: 'такое коробка коробки what box boxes',
  answer: copy(
    <>
      Обычно для жеребьёвки на «Тайного Санту» используют чью-нибудь шляпу, пакет или <b>коробку</b>
      , в которую все участники скидывают свои имена.
      <br />
      Коробка на этом сайте представляет собой место, куда участники игры "сбрасывают" свои карточки
      с именами для участия и где происходит жеребьёвка.
    </>,
    <>
      Usually, for the "Secret Santa" draw, someone's hat, bag, or a <b>box</b> is used, into which
      all participants drop their names.
      <br />
      The box on this site represents a place where game participants "drop off" their cards with
      names for participation and where the draw takes place.
    </>,
  ),
};

const qWhoIsOrganizer = {
  question: copy('Кто такой «организатор коробки»?', 'Who is the "box organizer"?'),
  search: 'связаться организатором огранизатор contact the organizer organizer',
  answer: copy(
    <>
      Организатор — человек, который создаёт игру и помогает ей пройти без путаницы. Он приглашает
      участников, настраивает правила и исключения, проводит жеребьёвку и следит за ходом обмена.
      <br />
      <br />
      Организатор может видеть статусы участников, помогать им в специальном чате и при необходимости
      проверить распределение Тайных Сант — например, если у кого-то возник вопрос или проблема с
      подарком.
    </>,
    <>
      The organizer creates the game and helps it run smoothly. They invite people, set rules and
      exclusions, run the draw, and follow the exchange.
      <br />
      <br />
      The organizer can see participant statuses, help in a special chat, and check who is whose
      Secret Santa if someone has a question or a problem with a gift.
    </>,
  ),
};

const qHowToPlay = {
  question: copy(
    'Как организовать «Тайного Санту» с помощью этого сайта?',
    'How to organize "Secret Santa" using this site?',
  ),
  search: 'как создать игру how to create a game',
  answer: copy(
    <>
      <b>Шаг 1.</b> Создайте игру: придумайте название, установите бюджет подарка и при необходимости
      настройте правила.
      <br />
      <br />
      <b>Шаг 2.</b> Отправьте участникам ссылку. Регистрация и email не обязательны — каждый сможет
      присоединиться и написать письмо Санте со своими пожеланиями.
      <br />
      <br />
      <b>Шаг 3.</b> Когда все будут готовы, настройте исключения и проведите жеребьёвку. Например,
      можно сделать так, чтобы определённые участники не выпали друг другу.
      <br />
      <br />
      <b>Шаг 4.</b> Следите за ходом игры: смотрите статусы участников, помогайте им в чате и при
      необходимости проверяйте, кто кому дарит подарок.
    </>,
    <>
      <b>Step 1.</b> Create a game: pick a name, set a gift budget, and adjust the rules if you
      need to.
      <br />
      <br />
      <b>Step 2.</b> Send people a link. Registration and email are optional — everyone can join
      and write a letter to Santa with their wishes.
      <br />
      <br />
      <b>Step 3.</b> When everyone is ready, set exclusions and run the draw. For example, you can
      keep certain people from drawing each other.
      <br />
      <br />
      <b>Step 4.</b> Follow the game: check participant statuses, help in chat, and look up who is
      giving to whom if you need to.
    </>,
  ),
};

const qBoxLimit = {
  question: copy(
    'Есть ли ограничение на количество участников в коробке?',
    'Is there a limit on the number of participants in a box?',
  ),
  search:
    'сколько участников максимальное максимум ограничение how many participants maximum limit',
  answer: copy(
    'Ограничения на количество участников в коробке нет',
    'There is no limit on the number of participants in the box draw',
  ),
};

const qCanIDrawWithoutRegister = {
  question: copy(
    'Можно ли провести жеребьёвку без создания коробки и регистрации участников?',
    'Is it possible to conduct a draw without creating a box and registering participants?',
  ),
  search: 'быстрая жеребьёвка регистрации без аккаунта quick draw without registration no account',
  answer: copy(
    <>
      Да. Если нужна только жеребьёвка без вишлистов и чата, проведите{' '}
      {faqLink('/randomizer', 'Быструю жеребьёвку')}: добавьте имена, при необходимости настройте
      исключения и получите отдельную ссылку с результатом для каждого. Регистрация и email не
      нужны — ссылки можно отправить в мессенджере или общем чате.
      <br />
      <br />
      В полной игре участники ещё пишут пожелания, указывают адрес и общаются в анонимном чате, а
      организатор следит за ходом обмена.
      <p>
        В быстрой жеребьёвке можно пригласить до <b>100 человек</b>.
      </p>
    </>,
    <>
      Yes. If you only need the draw — without wish lists or chat — use{' '}
      {faqLink('/randomizer', 'Quick Draw')}: add names, set exclusions if you need them, and get a
      personal result link for each person. No registration or email: you can share the links in a
      messenger or group chat.
      <br />
      <br />
      The full game also lets people write wishes, add an address, and use anonymous chat, while
      the organizer follows the exchange.
      <p>
        Quick Draw supports up to <b>100 people</b>.
      </p>
    </>,
  ),
};

const qMoney = {
  question: copy('Это бесплатный сервис?', 'Is this a free service?'),
  search: 'оплата сколько бесплатн стоит цена стоимость payment how much does it cost price',
  answer: copy(
    'Сайт бесплатный для всех, независимо от количества участников.',
    'The site is free for everyone, regardless of the number of participants.',
  ),
};

const qMinUsers = {
  question: copy(
    'Сколько участников минимально необходимо для проведения жеребьёвки?',
    'What is the minimum number of participants required for the draw?',
  ),
  search: 'минимум участников минимальное количество minimum participants minimum number',
  answer: copy(
    'Для того чтобы жеребьёвка была хоть немного секретной, необходимо, как минимум, три участника.',
    'For the draw to be somewhat secretive, a minimum of three participants is required.',
  ),
};

const qOrgCanParticipate = {
  question: copy(
    'Могу ли я, как организатор, тоже участвовать в жеребьёвке и не знать, кто кому попался?',
    'As an organizer, can I also participate in the draw and not know who got whom?',
  ),
  search: 'участие организатор participation organizer',
  answer: copy(
    <>
      Да, вы можете быть и организатором, и участником.
      <br />
      Пары в режиме организатора скрыты, пока вы сами не нажмёте «Показать» у подопечного или Санты
      и не подтвердите «Раскрыть секрет». Если знать распределение не хотите — просто не раскрывайте
      его.
    </>,
    <>
      Yes, you can be both the organizer and a participant.
      <br />
      In organizer view the pairs stay hidden until you tap “Show” on a giftee or Santa and confirm
      “Reveal the secret.” If you do not want to know the matches, do not reveal them.
    </>,
  ),
};

const qHowToKnowSantas = {
  question: copy(
    'Как посмотреть, кто кому попался в качестве Тайного Санты?',
    'How can I see who got whom as a Secret Santa?',
  ),
  search: 'чей санта секрет вижу настроек whose santa secret see settings',
  answer: copy(
    <>
      На странице игры откройте режим организатора — баннер «Перейти в режим организатора». В
      таблице нажмите «Показать» у подопечного или Санты и подтвердите «Раскрыть секрет».
      <br />
      Если режима организатора нет, вы, скорее всего, в другом профиле: откройте переключатель
      профилей в шапке.
    </>,
    <>
      On the game page, switch to organizer view — the “Switch to organizer mode” banner. In the
      table, tap “Show” on a giftee or Santa and confirm “Reveal the secret.”
      <br />
      If you do not see organizer view, you are probably in another profile: use the profile
      switcher in the header.
    </>,
  ),
};

const qOrgCanSetupDraw = {
  question: copy(
    'Как настроить жеребьёвку, чтобы участники не попадались друг другу? Как указать подопечного вручную?',
    "How can I set up the draw so that participants don't get each other? How can I manually assign a recipient?",
  ),
  search:
    'настроить жена муж супруги парень девушке указать подопечных попался другому вижу настроек исключения exclusions assign set up spouse boyfriend girlfriend recipients got each other see settings',
  answer: copy(
    <>
      В режиме организатора у нужного человека нажмите «Настроить исключения». Отметьте, кому этот
      участник не должен дарить подарок. Для супругов или другой
      пары включите взаимное исключение — тогда они не выпадут друг другу. Остальные участники
      распределятся случайно.
      <br />
      Подопечного можно назначить вручную в действиях участника («Назначить подопечного») — обычно
      после жеребьёвки или если кто-то присоединился позже.
      <br />
      Чтобы никто не дарил подарок тому, кто дарит ему, включите «Круговую жеребьёвку» в настройках
      игры.
      <br />
      <br />
      <b>Участники не узнают, что вы настроили жеребьёвку каким-либо образом.</b>
    </>,
    <>
      In organizer view, tap “Set exclusions” next to the person you want to adjust. Choose who
      they should not gift. For spouses or any other pair,
      turn on mutual exclusion so they will not draw each other. Everyone else is assigned at
      random.
      <br />
      You can also assign a giftee by hand from a participant’s actions (“Set giftee”) — usually
      after the draw, or if someone joined late.
      <br />
      To stop two people from simply drawing each other, turn on “Circle draw” in the game
      settings.
      <br />
      <br />
      <b>Participants will not know that you set up the draw in any particular way.</b>
    </>,
  ),
};

const qOrgCannotSeeSettings = {
  question: copy(
    'Я организатор коробки, но не вижу кнопки "провести жеребьёвку", что делать?',
    'I am the organizer of the box, but I don\'t see the "conduct the draw" button, what should I do?',
  ),
  search: 'вижу настроек настроек где жеребьёвка see settings settings where draw',
  answer: copy(
    <>
      <p>
        Жеребьёвка запускается плиткой «Жеребьёвка» в шапке игры. Если её нет, игру чаще всего
        открыли в другом профиле: в шапке выберите профиль, которым создавали игру — тогда появится
        режим организатора и запуск жеребьёвки.
      </p>
      <p>
        Если вы участник и хотите написать организатору, это можно сделать в специальном чате на
        странице игры.
      </p>
    </>,
    <>
      <p>
        The draw starts from the “Draw” chip in the game header. If you do not see it, the game
        was probably opened in another profile: use the profile switcher and pick the profile that
        created the game — then organizer view and the draw action appear.
      </p>
      <p>
        If you are a participant and want to reach the organizer, use the organizer chat on the
        game page.
      </p>
    </>,
  ),
};

const qAddUsers = {
  question: copy(
    'Как добавить еще участников? Можно ли добавить участников после проведения жеребьёвки?',
    'How do I add more participants? Can I add participants after the draw has been conducted?',
  ),
  search: 'добавить участника больше людей пригласить add participant more people invite',
  answer: copy(
    <>
      В таблице участников нажмите «+ Добавить участников» — можно пригласить по ссылке или добавить
      людей вручную.
      <br />
      <br />
      <b>Важно:</b> если жеребьёвка уже прошла, у новичка не будет подопечного сразу. Назначьте
      подопечного в действиях участника или выберите новых участников в таблице и проведите
      дополнительную жеребьёвку между ними.
    </>,
    <>
      On the participants table, tap “+ Add participants” — you can invite by link or add people
      by hand.
      <br />
      <br />
      <b>Important:</b> if the draw has already run, a new person will not get a giftee right
      away. Set a giftee from that participant’s actions, or select the new people in the table
      and run an additional draw among them.
    </>,
  ),
};

const qEditDeleteUser = {
  question: copy(
    'Как редактировать карточку участника? Как отправить приглашение на email? Как удалить карточку участника?',
    "How can I edit a participant's card? How can I send an invite by email? How can I delete a participant's card?",
  ),
  search: 'обновить карточку удал удалить email почта пригласить update card delete',
  answer: copy(
    <>
      В режиме организатора откройте действия участника. Можно изменить имя, скопировать ссылку,
      отправить приглашение на любой email, напомнить, настроить исключения и удалить из обмена.
      <br />
      В карточке нет поля email: «Пригласить по email» просто отправляет ссылку на указанный адрес.
      Пожелания, картинку и адрес участник меняет сам.
      <br />
      Удаление — пункт «Удалить участника» в тех же действиях.
    </>,
    <>
      In organizer view, open that participant’s actions. You can change the name, copy a link,
      send an invite to any email, send a reminder, set exclusions, and remove them from the
      exchange.
      <br />
      There is no email field on the card: “Invite by email” just sends the link to the address
      you type. Wishes, picture, and address are edited by the participant.
      <br />
      To remove them, choose “Delete participant” in the same menu.
    </>,
  ),
};

const qDeleteUserAfterDraw = {
  question: copy(
    'Можно ли удалить участника после проведения жеребьёвки? Как это сделать?',
    'Can I delete a participant after the draw has been conducted? How can I do this?',
  ),
  search: 'обновить карточку удал удалите удалить update card delete',
  answer: copy(
    <>
      Да, участника можно удалить и после жеребьёвки. В режиме организатора откройте действия
      строки и нажмите «Удалить участника».
      <br />
      <br />
      Тайному Санте удаленного участника достанется его подопечный, а в случае, когда Тайный Санта и
      подопечный дарят подарки друг другу, новый подопечный будет выбран случайным образом из
      оставшихся участников. Например, если жеребьёвка выглядит следующим образом: Вася → Петя →
      Маша → Катя → Вася, то если Петя передумал участвовать, Васе достанется новый подопечный ー
      Маша. Тайному Санте удаленного участника придет уведомление о смене подопечного.
    </>,
    <>
      Yes, you can remove a participant even after the draw. In organizer view, open that row’s
      actions and tap “Delete participant.”
      <br />
      <br />
      The Secret Santa of the deleted participant will receive their recipient, and in the case
      where the Secret Santa and the recipient give gifts to each other, a new recipient will be
      chosen randomly from the remaining participants. For example, if the draw looks like this:
      Vasya → Petya → Masha → Katya → Vasya, then if Petya decides not to participate, Vasya will
      get a new recipient — Masha. The Secret Santa of the deleted participant will receive a
      notification about the change of recipient.
    </>,
  ),
};

const qMultipleBoxes = {
  question: copy(
    'Можно ли участвовать в нескольких коробках?',
    'Can I participate in multiple boxes?',
  ),
  search: 'несколько коробок',
  answer: copy(
    <>
      Да, вы можете принимать участие в неограниченном количестве игр, причем, вы можете быть как
      участником, так и организатором в разных коробках.
    </>,
    <>
      Yes, you can participate in an unlimited number of games, and you can be both a participant
      and an organizer in different boxes.
    </>,
  ),
};

const qAdminVsParticipantBoxes = {
  question: copy(
    'Можно ли быть организатором в одной коробке, и участником в другой?',
    'Can I be an organizer in one box and a participant in another?',
  ),
  search: 'несколько коробок multiple boxes',
  answer: copy(
    <>
      Да, вы можете организовать одну игру сами, а принимать участие в другой игре. Также вы можете
      быть одновременно и участником и организатором игры.
    </>,
    <>
      Yes, you can organize one game and participate in another game. Also, you can be both a
      participant and an organizer in a game simultaneously.
    </>,
  ),
};

const qWaitingConfirmation = {
  question: copy(
    'Что означает "Ожидается подтверждение участия" на карточке участника?',
    'What does "Awaiting participation confirmation" mean on a participant\'s card?',
  ),
  search:
    'подтвердить email подтвердить емейл подтверждение подтв участ confirm email confirm email confirmation confirm participant',
  answer: copy(
    <>
      Надпись «Ожидается подтверждение участия» значит, что человек ещё не открыл своё приглашение
      и не заполнил карточку. Так бывает, если его пригласили по email или добавили в таблицу, но
      он пока не перешёл по ссылке.
      <br />
      <br />
      Если участник присоединился по общей ссылке и заполнил карточку, подтверждение уже не нужно.
      Организатор не может подтвердить участие за другого человека, но жеребьёвку можно провести и
      с неподтверждёнными карточками — они всё равно попадут в распределение.
    </>,
    <>
      “Awaiting participation confirmation” means the person has not opened their invite and
      finished their card yet. That happens after an email invite or after you add someone in the
      table, if they have not followed the link.
      <br />
      <br />
      If someone joined by the shared link and filled in their card, no extra confirmation is
      needed. The organizer cannot confirm for them, but you can still run the draw — unconfirmed
      cards are included and they can finish later.
    </>,
  ),
};

const qHowToChangeCard = {
  question: copy(
    'Как редактировать картинку, пожелания и адрес в моей карточке? Можно ли менять пожелания после проведения жеребьёвки?',
    'How can I edit the picture, wishes, and address in my card? Can I change my wishes after the draw has been conducted?',
  ),
  search:
    'обновить карточку как можно менять увидит изменения update card how can change will see changes',
  answer: copy(
    <>
      Менять карточку можно в любой момент — и до жеребьёвки, и после. Если жеребьёвка уже прошла,
      Тайный Санта увидит изменение в чате.
      <br />
      Откройте «Мой обмен», затем свою карточку и нажмите редактирование (карандаш). Там можно
      поменять картинку, пожелания и адрес.
    </>,
    <>
      You can change your card at any time — before or after the draw. If the draw has already
      run, your Secret Santa will see the change in chat.
      <br />
      Open “My exchange,” then your card, and tap edit (the pencil). There you can change the
      picture, wishes, and address.
    </>,
  ),
};

const qNoPostalAddress = {
  question: copy(
    'Я не вижу почтового адреса своего подопечного, почему?',
    'I do not see the postal address of my recipient, why?',
  ),
  search:
    'где адрес почтовый куда отправлять подарок какой адрес where is address postal where to send gift which address',
  answer: copy(
    <>
      Вы можете не видеть адреса подопечного по двум причинам:
      <ul>
        <li>
          Ваш подопечный пока что не заполнил свою карточку. В этом случае на карточке будет
          написано, что адрес не был заполнен. Вы можете поторопить подопечного в чате.
        </li>
        <li>
          Организатор не включил «Запросить контактные данные» в настройках игры. Тогда адреса нет
          и у вас в «Моём обмене». Попросите организатора включить эту опцию.
        </li>
      </ul>
    </>,
    <>
      You may not see your recipient's address for two reasons:
      <ul>
        <li>
          Your recipient has not yet filled out their card. In this case, the card will state that
          the address has not been filled in. You can hurry your recipient in the chat.
        </li>
        <li>
          The organizer did not turn on “Request contact details” in the game settings. Then your
          own card in “My exchange” will also have no address. Ask the organizer to enable it.
        </li>
      </ul>
    </>,
  ),
};

const qWillIKnowMySanta = {
  question: copy(
    'Может ли участник узнать, кто его Тайный Санта?',
    'Can a participant find out who their Secret Santa is?',
  ),
  search: 'узнать санту секрет find out santa secret',
  answer: copy(
    <>
      Участник может узнать, кто его Тайный Санта, только в двух случаях: если организатор сообщит
      ему об этом или если Тайный Санта сам выдаст себя через чат (сообщит свое имя намеренно) или
      иным образом.
    </>,
    <>
      A participant can find out who their Secret Santa is only in two cases: if the organizer tells
      them or if the Secret Santa reveals themselves through chat (intentionally disclosing their
      identity) or in some other way.
    </>,
  ),
};

const qCanSantaBeWard = {
  question: copy(
    'Может ли случиться так, что Тайный Санта и подопечный дарят подарок друг другу?',
    'Can it happen that the Secret Santa and the recipient give gifts to each other?',
  ),
  search: 'друг другу дарят санта подопечный give each other gifts santa recipient',
  answer: copy(
    <>
      Да, обычная жеребьёвка это допускает. Чтобы этого не случилось, организатор может включить
      «Круговую жеребьёвку» в настройках игры — тогда участники дарят подарки по кругу, а не друг
      другу. Для конкретных пар (например, супругов) лучше заранее настроить взаимное исключение в
      таблице участников.
    </>,
    <>
      Yes, a regular draw can produce that. To prevent it, the organizer can turn on “Circle draw”
      in the game settings — then people gift in a circle instead of drawing each other. For
      specific pairs (spouses, for example), set a mutual exclusion on the participants table
      beforehand.
    </>,
  ),
};

const qChangedMyMind = {
  question: copy(
    'Я передумал(а) участвовать в игре, как можно удалить карточку?',
    'I changed my mind about participating in the game, how can I delete my card?',
  ),
  search: 'хочу удал удалить удалите карточку как want to delete card how',
  answer: copy(
    <>
      Откройте «Мой обмен», затем свою карточку и нажмите редактирование. Внизу страницы можно
      удалить карточку — и до жеребьёвки, и после.
      <br />
      <br />
      Если жеребьёвка уже прошла, удаление нарушит пары: у вашего Санты сменится подопечный, а
      организатор получит сообщение в чат. Если игра уже идёт, лучше сначала написать организатору.
    </>,
    <>
      Open “My exchange,” then your card, and tap edit. At the bottom you can delete the card —
      before or after the draw.
      <br />
      <br />
      After the draw, deleting breaks the pairs: your Santa gets a new giftee, and the organizer
      gets a message in chat. If the game is already running, write to the organizer first.
    </>,
  ),
};

const qWhoToAskQuestions = {
  question: copy(
    'К кому обращаться с вопросами по игре?',
    'Who should I contact with questions about the game?',
  ),
  search:
    'задать вопрос кому когда жеребьёвка куда отдать подарок положить под елку передумал участвовать где празднуем ask question whom when draw where give gift put under tree changed mind participating where celebrate',
  answer: copy(
    <>
      Если вопрос про конкретную игру, а не про работу сайта, напишите организатору в чате на
      странице игры. Примеры вопросов, на которые он может ответить:
      <ul>
        <li>Когда мы будем дарить подарки?</li>
        <li>
          Подскажите, как будет происходить вручение подарка? Пересыл по внутренней почте между
          офисами или сторонней доставкой за свой счет?
        </li>
        <li>Меня не будет в офисе 25 декабря, куда мне положить подарок?</li>
        <li>Я передумал участвовать в игре, пожалуйста, удалите мою карточку</li>
        <li>Здравствуйте, я хочу отменить свое участие в тайном санте</li>
        <li>А когда нужно принести подарок или когда планируется вручение?)</li>
      </ul>
    </>,
    <>
      If your question is about a specific game, not the website, write to the organizer in the
      chat on the game page. Examples of questions they can answer:
      <ul>
        <li>When will we be giving gifts?</li>
        <li>
          Could you tell me how the gift presentation will take place? Will it be through internal
          mail between offices or with external delivery at our own expense?
        </li>
        <li>I won't be in the office on December 25th, where should I leave the gift?</li>
        <li>I changed my mind about participating in the game, please delete my card</li>
        <li>Hello, I want to cancel my participation in Secret Santa</li>
        <li>And when do we need to bring the gift or when is the presentation planned?)</li>
      </ul>
    </>,
  ),
};

const qChatAnonymous = {
  question: copy('Анонимный ли чат с подопечным?', 'Is the chat with the recipient anonymous?'),
  search: 'узнает санта подопечный анонимный чат сообщения диалог anonymous chat messages dialogue',
  answer: copy(
    'Да, чат с подопечным анонимный, он не увидит вашего имени или аватарки.',
    'Yes, the chat with the recipient is anonymous; they will not see your name or avatar.',
  ),
};

const qDrawAnonymous = {
  question: copy(
    'Если настроить жеребьёвку вручную, участники узнают об этом?',
    'If I manually set up the draw, will the participants know about it?',
  ),
  search:
    'секретная настройка настрои указ указать вручную самостоят самостоятельно secret setting manually independently',
  answer: copy(
    'Нет, участники не узнают, настроили ли вы жеребьёвку или она была полностью случайной.',
    'No, participants will not know whether you set up the draw or if it was completely random.',
  ),
};

const qAdditionalDraw = {
  question: copy(
    'Как добавить еще участников после проведения жеребьёвки? Как провести дополнительную жеребьёвку между новыми участниками?',
    'How can I add more participants after the draw has been conducted? How to conduct an additional draw among the new participants?',
  ),
  search: 'пригласить добавить больше участников invite add more participants',
  answer: copy(
    <>
      После жеребьёвки по общей ссылке уже нельзя присоединиться самостоятельно. Организатор
      добавляет новичков кнопкой «+ Добавить участников» в таблице. Затем назначьте подопечного в
      действиях участника — или выберите новых людей в таблице и проведите дополнительную
      жеребьёвку только между ними. Уже назначенные пары не изменятся.
    </>,
    <>
      After the draw, people can no longer join on their own with the shared link. The organizer
      adds newcomers with “+ Add participants” on the table. Then set a giftee from that
      participant’s actions — or select the new people in the table and run an additional draw
      among them only. Existing matches stay as they are.
    </>,
  ),
};

const qPicture = {
  question: copy(
    'Как отправить картинку в чат с подопечным/сантой?',
    'How can I send a picture in the chat with my recipient/Santa?',
  ),
  search: 'картинка ссылка файл прикрепить picture link file attach',
  answer: copy(
    <>
      Слева от поля сообщения нажмите на иконку скрепки и выберите нужный файл. После выбора файла в
      чат отправится сообщение со ссылкой на него.
    </>,
    <>
      Click on the paperclip icon to the left of the message field and select the desired file.
      After selecting the file, a message with a link to it will be sent in the chat.
    </>,
  ),
};

const qSetWrongEmail = {
  question: copy(
    'Я указал(а) неверную почту в карточке, что делать?',
    'I have entered wrong email in my card, what to do?',
  ),
  search: 'указа неверн неверная почт в карточке делать entered wrong email',
  answer: copy(
    <>
      В карточке нет поля email. Если нужна ссылка на почту, напишите организатору — он отправит
      приглашение на любой адрес через «Пригласить по email». Игра работает и без писем: достаточно
      персональной ссылки.
    </>,
    <>
      There is no email field on the card. If you need the link in your inbox, ask the organizer —
      they can send the invite to any address with “Invite by email.” The game also works without
      mail: a personal link is enough.
    </>,
  ),
};

const qHowToDeleteCard = {
  question: copy('Как удалить свою карточку из коробки?', 'How to delete my card from the box?'),
  search: 'удалить удал удалите карточ коробки delete card',
  answer: copy(
    <>
      Откройте «Мой обмен», свою карточку и редактирование. Внизу страницы есть удаление — оно
      доступно и до жеребьёвки, и после.
      <br />
      После жеребьёвки пары нарушатся, а организатор получит сообщение в чат. Если игра уже идёт,
      лучше сначала написать ему.
    </>,
    <>
      Open “My exchange,” your card, and edit. Delete is at the bottom of that page — before or
      after the draw.
      <br />
      After the draw the pairs will break, and the organizer gets a chat message. If the game is
      already running, write to them first.
    </>,
  ),
};

const qHowToDeleteParticipantCard = {
  question: copy(
    'Как удалить карточку участника из коробки?',
    'How to delete participant card from the box?',
  ),
  search: 'удал удалить удал удалите карточку участника коробки delete participant card',
  answer: copy(
    <>
      В режиме организатора откройте действия участника и нажмите «Удалить участника».
      <br />
      <b>Важно:</b> если жеребьёвка уже прошла, у Тайного Санты удалённого человека сменится
      подопечный.
    </>,
    <>
      In organizer view, open that participant’s actions and tap “Delete participant.”
      <br />
      <b>Important:</b> if the draw has already run, the Secret Santa of the deleted person will
      get a different giftee.
    </>,
  ),
};

const qSecondOrganizer = {
  question: copy(
    'Можно ли добавить второго организатора в коробку?',
    'Can I add additional organizer to the box?',
  ),
  search: 'организ организатора организатором добав добавить organizer organi',
  answer: copy(
    <>Добавить второго организатора в коробку на данный момент нельзя.</>,
    <>Currently, you cannot add additional organizer to the box.</>,
  ),
};

const qChangeOrganizer = {
  question: copy('Можно ли сменить организатора коробки?', 'Can I change organizer of the box?'),
  search:
    'организ организатора организатором сменить заменить замен помен поменять добав добавить organizer organi change',
  answer: copy(
    <>
      Если нужно сменить организатора и вы сами им являетесь, напишите на{' '}
      <b>postman@santa-secret.ru</b>: ссылка на игру и имя человека, которому хотите передать
      организацию.
    </>,
    <>
      If you need to change the organizer and you are the current one, write to{' '}
      <b>postman@santa-secret.com</b> with a link to the game and the name of the person who should
      take over.
    </>,
  ),
};



const qNeedRegistration = {
  question: copy('Нужны ли регистрация и email для участия?', 'Do I need to register or give an email?'),
  search: 'регистрация email почта аккаунт без регистрации без почты',
  answer: copy(
    <>
      Нет. Организатор и участники могут пройти всю игру без обязательной регистрации и email:
      создать игру, присоединиться по ссылке, заполнить пожелания, провести жеребьёвку и продолжить
      обмен подарками.
      <br />
      <br />
      Аккаунт и email можно использовать по желанию, но для полноценной игры они не обязательны.
    </>,
    <>
      No. The organizer and participants can play the full game without a required account or email:
      create a game, join by link, add wishes, run the draw, and continue the gift exchange.
      <br />
      <br />
      An account and email are optional — they are not required for a complete game.
    </>,
  ),
};


export const FAQ_SECTIONS = [
  {
    id: 'common',
    titleKey: 'faq.common',
    questions: [
      qNeedRegistration,
      qSetWrongEmail,
      qNoEmailInbox,
      qWhoToAskQuestions,
      qHowToChangeCard,
      qHowToDeleteCard,
      qWaitingConfirmation,
      qChangedMyMind,
      qNoPostalAddress,
      qWhenIWillKnowWard,
      qWillIKnowMySanta,
      qChatAnonymous,
      qPicture,
      qCanSantaBeWard,
      qOddNumber,
      qCanIDrawWithoutRegister,
      qMinUsers,
      qMultipleBoxes,
      qAdminVsParticipantBoxes,
    ],
  },
  {
    id: 'organizers',
    titleKey: 'faq.organizers',
    questions: [
      qBoxLimit,
      qNoEmailInboxOrg,
      qOrgCanSetupDraw,
      qDrawAnonymous,
      qChangeOrganizer,
      qSecondOrganizer,
      qAddUsers,
      qAdditionalDraw,
      qHowToDeleteParticipantCard,
      qEditDeleteUser,
      qDeleteUserAfterDraw,
      qHowToKnowSantas,
      qOrgCannotSeeSettings,
      qOrgCanParticipate,
    ],
  },
  {
    id: 'service',
    titleKey: 'faq.service',
    questions: [
      qWhatIsSanta,
      qWhatIsThisSiteFor,
      qWhatIsTheBox,
      qWhoIsOrganizer,
      qHowToPlay,
      qMoney,
    ],
  },
] as const;
